import { memo } from 'react';

import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { useWidgetRenderContext } from '@/components/ui/widget/widgetRenderContext';
import { formatNumber } from '@/utils';
import { getByPath } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import type { NumberView } from '../number/number.type';
import { Progress } from './progress/progress';
import type { StatusView } from './status.type';

const DEFAULT_PROGRESS_SIZE = 16 as const;

type Props = {
  view: StatusView;
  data: WidgetData;
};

type Status = {
  value: string;
  progress: number;
};

const StatusViewImpl = memo(({ view, data }: Props) => {
  const { path, progressPath, progressSize, cancelValue = 0 } = view;

  const renderContext = useWidgetRenderContext();

  const status: Status = {
    value: getByPath<string>(data, path) ?? 'Status not found',
    progress: getByPath<number>(data, progressPath) ?? -1
  };

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex size-full items-center',
              renderContext === 'table-cell' && 'px-[5px]'
            )}
          >
            <div className="flex w-full items-center gap-3">
              <div className="flex shrink-0 items-center justify-center">
                <Progress
                  cancelValue={cancelValue}
                  progress={status.progress}
                  size={progressSize || DEFAULT_PROGRESS_SIZE}
                />
              </div>
              <div className="-mb-[2px] grow truncate">{status.value}</div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent align="start" side="bottom" variant="contrast">
          <p>
            Progress:{' '}
            {formatNumber(status.progress, {
              options: { style: 'percent', minimumFractionDigits: 0 }
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

// sort defaults to text on path
const getSortConfig = (view: StatusView): NumberView => ({
  type: 'number',
  path: view.progressPath
});

// filtering defaults to text search on path
const getFilterConfig = (view: StatusView) => ({
  type: 'text',
  path: view.progressPath,
  placeholder: 'Eg >0.5, 0.25<<0.75',
  interpreter: {
    type: 'number'
  }
});

// width defaults to 'medium'
const getDefaultWidth = (): ColumnWidth => 'small';

// stringifies data at path
const getStringifyFunction = (view: StatusView) => {
  const { path } = view;
  return (data: WidgetData) => getByPath<string>(data, path);
};

export const statusViewProvider = {
  component: StatusViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
